import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Typography } from '@mui/material';
import { type ChangeEvent, forwardRef, type InputHTMLAttributes, useState } from 'react';

import { Button } from '../Button/Button';
import { HiddenInput } from '../HiddenInput/HiddenInput';

type FilePickerProps = InputHTMLAttributes<HTMLInputElement> & {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  label?: string;
  showSelectedFileName?: boolean;
  error?: string | null;
  icon?: React.ReactNode;
  buttonProps?: React.ComponentProps<typeof Button>;
  hide?: boolean;
  multiple?: boolean;
};

export const FilePicker = forwardRef(function FilePicker(
  {
    onChange,
    accept,
    error,
    multiple,
    label = 'Pick a file',
    showSelectedFileName = false,
    icon,
    buttonProps = {
      disabled: false,
      fullWidth: false,
      size: 'medium',
    },
    hide = false,
    ...rest
  }: FilePickerProps,
  ref: React.Ref<HTMLInputElement>
) {
  const [filename, setFilename] = useState<string | null>(null);
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file?.name) {
      setFilename(file?.name);
    }
    onChange(event);
  };

  const boxProps = hide ? { sx: { display: 'none' } } : {};
  const startIcon = icon ? icon : <CloudUploadIcon />;

  return (
    <Box {...boxProps}>
      <Button component="label" role={undefined} size="large" tabIndex={-1} startIcon={startIcon} {...buttonProps}>
        {label}
        <HiddenInput multiple={multiple} ref={ref} type="file" accept={accept} onChange={handleFileChange} {...rest} />
      </Button>
      {error ? (
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          {error}
        </Typography>
      ) : null}
      {showSelectedFileName && filename ? (
        <Typography variant="body2" sx={{ color: 'grey', display: 'inline-block', ml: 2 }}>
          {filename}
        </Typography>
      ) : null}
    </Box>
  );
});
